import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * LoginPage — Google Sign-In is the ONLY normal path. The master-password
 * form is hidden behind a "recovery" toggle and the backend returns 403
 * unless EMERGENCY_LOGIN_ENABLED=true is set on the server.
 *
 * The Google callback lands on this page with `#session_id=XYZ`. We detect
 * that, exchange it for our own JWT, and route to /.
 */
export default function LoginPage() {
  const { user, login, loginWithGoogleSessionId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [showRecovery, setShowRecovery] = useState(false);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [exchanging, setExchanging] = useState(false);
  const exchangedRef = useRef(false);

  // If AuthProvider stashed an error while processing a Google callback
  // (e.g. wrong email → 403), pick it up here and show it.
  useEffect(() => {
    const stored = sessionStorage.getItem("crm_login_error");
    if (stored) {
      setErr(stored);
      sessionStorage.removeItem("crm_login_error");
    }
  }, []);

  // If Google returned us here with #session_id=..., exchange it for a JWT.
  useEffect(() => {
    if (exchangedRef.current) return;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return;
    exchangedRef.current = true;
    setExchanging(true);
    (async () => {
      try {
        await loginWithGoogleSessionId(decodeURIComponent(m[1]));
        // Clear the hash so refresh doesn't retry
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      } catch (ex) {
        const status = ex?.response?.status;
        const detail = ex?.response?.data?.detail;
        if (status === 403) {
          setErr(
            typeof detail === "string"
              ? detail
              : "This Google account is not authorised to access this CRM."
          );
        } else if (status === 401) {
          setErr("Google session expired. Please try again.");
        } else {
          setErr("Google Sign-In failed. Please try again.");
        }
        window.history.replaceState(null, "", window.location.pathname);
        setExchanging(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  if (user === undefined) return null;
  if (user) {
    const dest = location.state?.from?.pathname || "/";
    return <Navigate to={dest} replace />;
  }

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const submitRecovery = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(password);
    } catch (ex) {
      const status = ex?.response?.status;
      if (status === 403) {
        setErr("Emergency login is disabled. Contact the app administrator.");
      } else if (status === 429) {
        setErr("Too many attempts. Please wait a few minutes.");
      } else if (status === 401) {
        setErr("Incorrect password.");
      } else {
        setErr("Login failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0a0a0a] text-white flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-40 -left-40 w-[440px] h-[440px] rounded-full bg-[#ff2a8f]/25 blur-[100px]" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#00e5ff]/20 blur-[100px]" />
      </div>

      <div
        className="relative w-full max-w-md space-y-6 rounded-2xl border border-white/10 bg-[#0f0f0f]/85 backdrop-blur-xl p-8 shadow-[0_10px_60px_rgba(0,0,0,0.6)]"
        data-testid="login-form"
      >
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#00e5ff]/10 border border-[#00e5ff]/40">
            <Lock className="h-6 w-6 text-[#00e5ff]" />
          </div>
          <h1 className="font-display uppercase tracking-[0.22em] text-xl">
            Enter Atlas
          </h1>
          <p className="text-white/50 text-xs font-mono">
            Private CRM · authorised Google account required
          </p>
        </div>

        {exchanging ? (
          <div
            className="flex flex-col items-center gap-3 py-4"
            data-testid="google-exchanging"
          >
            <Loader2 className="h-6 w-6 animate-spin text-[#00e5ff]" />
            <div className="text-xs font-mono text-white/60 uppercase tracking-widest">
              Signing you in…
            </div>
          </div>
        ) : (
          <Button
            type="button"
            onClick={startGoogle}
            className="w-full h-12 bg-white text-[#0a0a0a] hover:bg-white/90 flex items-center justify-center gap-3 font-display uppercase tracking-wider text-sm rounded-xl"
            data-testid="google-signin-btn"
          >
            <GoogleG /> Sign in with Google
          </Button>
        )}

        {err && (
          <div
            className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2"
            data-testid="login-error"
          >
            {err}
          </div>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowRecovery((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/30 hover:text-white/70 transition"
            data-testid="show-recovery-btn"
          >
            <ShieldAlert className="h-3 w-3" />
            {showRecovery ? "Hide" : "Emergency recovery"}
          </button>
        </div>

        {showRecovery && (
          <form onSubmit={submitRecovery} className="space-y-3 pt-2 border-t border-white/10">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">
              Emergency master-password login is currently disabled by policy.
              This form will 403 unless the server administrator has explicitly
              enabled recovery.
            </div>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Emergency password"
                className="bg-black/40 border-white/10 text-white h-11 pr-11"
                data-testid="login-password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/90 p-1.5 rounded-md transition"
                aria-label={show ? "Hide password" : "Show password"}
                data-testid="login-toggle-visibility"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              type="submit"
              disabled={busy || !password}
              className="w-full h-11 btn-neon"
              data-testid="login-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Unlocking…
                </>
              ) : (
                "Emergency unlock"
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
